import React from "react";
import ReactDOM from "react-dom";
import { ViewfinderCircleIcon } from "@heroicons/react/24/outline";

interface ExpandableImageProps {
  src: string;
  alt?: string;
  imageClassName?: string;
  imageContainerClassName?: string;
}

export const ExpandableImage: React.FC<ExpandableImageProps> = ({
  src,
  alt = "",
  imageClassName = "",
  imageContainerClassName = "",
}) => {
  const overlayRef = React.useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const handleExpandKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setIsExpanded(true);
    }
  };

  const handleCollapseKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
      e.preventDefault();
      setIsExpanded(false);
    }
  };

  // auto-focus on overlay when it's shown for accessibility
  React.useEffect(() => {
    if (isExpanded) {
      overlayRef.current?.focus();
    }
  }, [isExpanded]);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label="Expand image"
        data-testid="image-preview-container"
        className={`expandable-image__preview-container ${imageContainerClassName}`}
        onClick={() => setIsExpanded(true)}
        onKeyDown={handleExpandKeyDown}
      >
        <img
          src={src}
          alt={alt}
          data-testid="image-preview"
          className={`expandable-image__preview-image ${imageClassName}`}
        />
        <ViewfinderCircleIcon data-testid="expand-icon" className="expandable-image__expand-icon" />
      </div>

      {isExpanded &&
        ReactDOM.createPortal(
          <div
            ref={overlayRef}
            role="button"
            tabIndex={0}
            aria-label="Close expanded image"
            data-testid="overlay"
            className="expandable-image__overlay"
            onClick={() => setIsExpanded(false)}
            onKeyDown={handleCollapseKeyDown}
          >
            <button
              type="button"
              aria-label="Close fullscreen image"
              data-testid="close-button"
              className="expandable-image__close-button"
            >
              &times;
            </button>
            <img
              src={src}
              alt={alt}
              data-testid="image-expanded"
              className="expandable-image__expanded-image"
            />
          </div>,
          document.body,
        )}
    </>
  );
};
